import { Form, Formik, FormikActions, FormikProps } from "formik";
import React from "react";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import * as yup from "yup";
import ErrorFocus from "../../../components/Form/ErrorFocus";
import InputField from "../../../components/Form/InputField";
import RadioGroup from "../../../components/Form/RadioGroup";
import SubmitButton from "../../../components/Form/SubmitButton";
import processSubmitErrors from "../../../helpers/processSubmitErrors";
import { emailRegex, phonePlus } from "../../../helpers/valid";
import { ShareReportParams, setShareReportPost } from '../../../services/setShareReportPost';
import styles from "./ShareReportForm.module.scss";

export interface FormValues {
  sendMethod: "email" | "phone";
  name: string;
  email: string;
  phone: string;
  message: string;
}

const ShareReportForm = () => {
  const initialValues: FormValues = {
    sendMethod: "email",
    name: "",
    email: "",
    phone: "",
    message: "",
  };

  const validationSchema = yup.object().shape({
    sendMethod: yup
      .string()
      .required("Elija una opción"),
    name: yup
      .string()
      .required("Se requiere el nombre del destinatario"),
    email: yup
      .string()
      .when("sendMethod", {
        is: value => value === "email",
        then: yup
          .string()
          .required("El correo es requerido")
          .email("El correo no es válido")
          .matches(emailRegex, "El correo no es válido")
      }),
    phone: yup
      .string()
      .when("sendMethod", {
        is: value => value === "phone",
        then: yup
          .string()
          .required("El teléfono es requerido")
          .matches(phonePlus, "El número de móvil no es válido")
      }),
    message: yup.string(),
  });

  const methods = [
    { name: "Correo", value: "email" },
    { name: "Teléfono móvil", value: "phone" },
  ];

  const handleSubmit = async (
    values: FormValues,
    {
      setErrors,
      setStatus,
      setSubmitting,
      resetForm
    }: FormikActions<FormValues>
  ) => {
    try {
      const sendMethod = values.sendMethod;
      const data: ShareReportParams = {
        sendMethod,
        name: values.name,
        email: sendMethod === "email" ? values.email : undefined,
        phone: sendMethod === "phone" ? values.phone : undefined,
        message: values.message,
      };
      await setShareReportPost(data);
      resetForm();
    } catch (error) {
      processSubmitErrors(error, values, setErrors, setStatus);
    }
    setSubmitting(false);
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        render={({ values }: FormikProps<FormValues>) => (
          <Form className={styles.form}>
            <RadioGroup
              name="sendMethod"
              label="¿Cómo deseas compartir tu reporte?"
              options={methods}
              className={styles.field}
            />
            <div className={styles.grid}>
              <InputField
                name="name"
                label="Nombre del destinatario"
                className={styles.field}
              />
              {values.sendMethod === "email"
                ? (
                  <InputField
                    name="email"
                    label="Correo del destinatario"
                    className={styles.field}
                  />
                )
                : (
                  <PhoneInput
                    country={'co'}
                    value={values.phone}
                    onChange={value => {
                      values.phone = value; // Guarda el número con código internacional
                    }}
                    enableSearch
                    inputProps={{
                      name: 'phone',
                      required: true
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '40px'
                    }}
                    containerClass={styles.field}
                  />
                )
              }
            </div>
            <InputField
              name="message"
              label="Mensaje"
              type="textarea"
              className={`${styles.field} ${styles.message}`}
            />
            <div className={styles.container}>
              <SubmitButton
                id="share-report"
                text="Compartir mi reporte"
                className={styles.button}
              />
            </div>
            <ErrorFocus />
          </Form>
        )}
      />
    </>
  );
};

export default ShareReportForm;
